import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";

var DEFAULT_VIEW = "timeGridWeek";

var DEFAULT_CONFIG = {
  plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
  headerToolbar: {
    left: "prev,next today",
    center: "title",
    right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
  },
  nowIndicator: true,
  slotMinTime: "07:00:00",
  slotMaxTime: "24:00:00",
  allDaySlot: true,
  allDayText: "",
  height: "auto",
  expandRows: true,
  slotDuration: "00:30:00",
  slotLabelInterval: "01:00:00",
  dayMaxEvents: 3,
  dayCellContent: function (arg) {
    return arg.dayNumberText.replace(".", "");
  },
  slotLabelFormat: { hour: "2-digit", minute: "2-digit", hour12: false },
  views: {
    dayGridMonth: {
      dayHeaderFormat: { weekday: "long" },
    },
    timeGridWeek: {
      dayHeaderFormat: { weekday: "long", day: "numeric", month: "numeric" },
    },
    timeGridDay: {
      dayHeaderFormat: { weekday: "long", day: "numeric", month: "numeric" },
    },
  },
  navLinks: true,
  weekNumbers: false,
  firstDay: 1,
};

function isPlainObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

function deepMerge(base, override) {
  var out = Array.isArray(base) ? base.slice() : Object.assign({}, base);
  Object.keys(override).forEach(function (key) {
    if (isPlainObject(out[key]) && isPlainObject(override[key])) {
      out[key] = deepMerge(out[key], override[key]);
    } else {
      out[key] = override[key];
    }
  });
  return out;
}

function localDateStr(date) {
  return (
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0")
  );
}

/**
 * Generic, reusable FullCalendar wrapper.
 *
 * Owns calendar instantiation/config, event fetching with range caching, the
 * custom agenda (listWeek) view, view/date URL state, the iCal copy button and
 * locale loading. Project-specific behaviour is layered on through the filter
 * pipeline (see filters.js for the FilterControl contract) and the returned
 * controller API.
 *
 * @returns {object} controller with getCalendar/getElement/registerFilter/
 *   getFilterState/onFilterChange/filterEvents/refresh and a `ready` promise.
 */
export function createCalendar(el, options) {
  options = options || {};

  var eventsUrl = options.eventsUrl || el.getAttribute("data-events-url");
  var detailUrl = options.detailUrl || el.getAttribute("data-detail-url") || "";
  var defaultView = options.defaultView || DEFAULT_VIEW;
  var initialView =
    options.initialView || el.getAttribute("data-initial-view") || defaultView;
  var initialDate =
    options.initialDate || el.getAttribute("data-initial-date") || undefined;
  var lang = document.documentElement.lang || "en";
  var agendaViewType = options.agendaViewType || "listWeek";
  var icalButtonId = options.icalButtonId || "ical-copy-btn";
  var icalUrlAttr = options.icalUrlAttr || "data-ical-url";

  var allEvents = [];
  var cachedStart = null;
  var cachedEnd = null;
  var calendar = null;
  // Registered filter controls (see filters.js for the contract)
  var filters = [];
  // Subscribers notified after any filter change
  var changeHandlers = [];

  // Custom agenda container
  var agendaContainer = document.createElement("div");
  agendaContainer.className = "fc-custom-agenda hidden";
  el.parentNode.insertBefore(agendaContainer, el.nextSibling);

  var weekdayFormatter = new Intl.DateTimeFormat(lang, { weekday: "short" });

  function filterEvents(events) {
    return events.filter(function (e) {
      return filters.every(function (control) {
        return typeof control.predicate === "function"
          ? control.predicate(e)
          : true;
      });
    });
  }

  function renderCustomAgenda(start, end, events) {
    var dayMap = {};
    var current = new Date(start);
    while (current < end) {
      var key =
        current.getFullYear() +
        "-" +
        String(current.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(current.getDate()).padStart(2, "0");
      dayMap[key] = { date: new Date(current), events: [] };
      current.setDate(current.getDate() + 1);
    }

    events.forEach(function (e) {
      var eStart = new Date(e.start);
      var eEnd = e.end
        ? new Date(e.end)
        : new Date(eStart.getTime() + 86400000);
      var from = eStart < start ? new Date(start) : new Date(eStart);
      var to = eEnd > end ? end : eEnd;
      from.setHours(0, 0, 0, 0);
      while (from < to) {
        var key =
          from.getFullYear() +
          "-" +
          String(from.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(from.getDate()).padStart(2, "0");
        if (dayMap[key]) {
          dayMap[key].events.push(e);
        }
        from.setDate(from.getDate() + 1);
      }
    });

    var html = "";
    Object.keys(dayMap)
      .sort()
      .forEach(function (key) {
        var day = dayMap[key];
        if (day.events.length === 0) return;
        var d = day.date;
        var isSun = d.getDay() === 0;
        var dayNum = d.getDate();
        var weekday = weekdayFormatter.format(d);

        html +=
          '<div class="fc-agenda-day' +
          (isSun ? " fc-agenda-day-sun" : "") +
          '">';
        html += '<div class="fc-agenda-date">';
        html += '<span class="fc-agenda-date-num">' + dayNum + "</span>";
        html += '<span class="fc-agenda-date-weekday">' + weekday + "</span>";
        html += "</div>";
        html += '<div class="fc-agenda-events">';

        day.events.forEach(function (e) {
          var color = e.color || e.backgroundColor || "#6b7280";
          var eventId = e.id || "";
          var slug = (e.extendedProps && e.extendedProps.slug) || "";
          var evDetailUrl =
            (e.extendedProps && e.extendedProps.detailUrl) || "";
          html +=
            '<div class="fc-agenda-event" data-event-id="' +
            eventId +
            '" data-slug="' +
            slug +
            '" data-detail-url="' +
            evDetailUrl +
            '">';
          html +=
            '<span class="fc-agenda-dot" style="background:' +
            color +
            '"></span>';
          html +=
            '<span class="fc-agenda-event-title">' +
            (e.title || "") +
            "</span>";
          html += "</div>";
        });

        html += "</div></div>";
      });

    agendaContainer.innerHTML = html;

    agendaContainer
      .querySelectorAll(".fc-agenda-event")
      .forEach(function (node) {
        node.addEventListener("click", function () {
          var dUrl = node.getAttribute("data-detail-url");
          if (dUrl) {
            window.location.href = dUrl;
          }
        });
      });
  }

  function refreshAgendaIfActive() {
    if (calendar && calendar.view.type === agendaViewType) {
      var view = calendar.view;
      renderCustomAgenda(
        view.activeStart,
        view.activeEnd,
        filterEvents(allEvents),
      );
    }
  }

  function rebuildIcalUrl() {
    var icalBtn = document.getElementById(icalButtonId);
    if (!icalBtn) return;

    var icalParams = new URLSearchParams();
    filters.forEach(function (control) {
      if (control.paramKey === false) return;
      if (control.serializeForIcal === false) return;
      var value =
        typeof control.serialize === "function" ? control.serialize() : null;
      if (value) icalParams.set(control.paramKey || control.key, value);
    });

    var icalParts = [];
    icalParams.forEach(function (value, key) {
      icalParts.push(key + "=" + value);
    });
    var icalQs = icalParts.join("&");
    var origin = window.location.origin;
    var basePath = icalBtn
      .getAttribute(icalUrlAttr)
      .replace(/\?.*$/, "")
      .replace(origin, "");
    icalBtn.setAttribute(
      icalUrlAttr,
      origin + basePath + (icalQs ? "?" + icalQs : ""),
    );
  }

  function pushStateToUrl() {
    if (!calendar) return;
    var params = new URLSearchParams();

    // View: omit if default
    var currentView = calendar.view.type;
    if (currentView !== defaultView) {
      params.set("view", currentView);
    }

    // Date: omit if today (use local date to avoid UTC shift)
    var currentDateStr = localDateStr(calendar.getDate());
    var todayStr = localDateStr(new Date());
    if (currentDateStr !== todayStr) {
      params.set("date", currentDateStr);
    }

    // Each registered control contributes its own param
    filters.forEach(function (control) {
      if (control.paramKey === false) return;
      var value =
        typeof control.serialize === "function" ? control.serialize() : null;
      if (value) params.set(control.paramKey || control.key, value);
    });

    var parts = [];
    params.forEach(function (value, key) {
      parts.push(key + "=" + value);
    });
    var qs = parts.join("&");
    var newUrl = window.location.pathname + (qs ? "?" + qs : "");
    history.replaceState(null, "", newUrl);

    // Keep the iCal button URL in sync with the active filters
    rebuildIcalUrl();
  }

  function notify(changedKey) {
    changeHandlers.forEach(function (handler) {
      handler(changedKey);
    });
  }

  function refresh() {
    if (calendar) calendar.refetchEvents();
    refreshAgendaIfActive();
    pushStateToUrl();
  }

  function triggerChange(changedKey) {
    notify(changedKey);
    refresh();
  }

  function registerFilter(control) {
    filters.push(control);
    if (typeof control.init === "function") {
      control.init({ controller: controller, triggerChange: triggerChange });
    }
  }

  function getFilterState(key) {
    for (var i = 0; i < filters.length; i++) {
      if (filters[i].key === key && typeof filters[i].getState === "function") {
        return filters[i].getState();
      }
    }
    return null;
  }

  function onFilterChange(handler) {
    changeHandlers.push(handler);
    return function () {
      var idx = changeHandlers.indexOf(handler);
      if (idx !== -1) changeHandlers.splice(idx, 1);
    };
  }

  function initIcalButton() {
    var icalCopyBtn = document.getElementById(icalButtonId);
    if (!icalCopyBtn) return;

    icalCopyBtn.addEventListener("click", function () {
      var url = icalCopyBtn.getAttribute(icalUrlAttr);
      var label = icalCopyBtn.querySelector(".ical-label");
      var original = label ? label.textContent : "";

      function showFeedback(text) {
        if (!label) return;
        label.textContent = text;
        setTimeout(function () {
          label.textContent = original;
        }, 2000);
      }

      function fallbackCopy(text) {
        var textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand("copy");
          showFeedback("✓ Copied!");
        } catch (e) {
          showFeedback("Failed to copy");
        }
        document.body.removeChild(textarea);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(
          function () {
            showFeedback("✓ Copied!");
          },
          function () {
            fallbackCopy(url);
          },
        );
      } else {
        fallbackCopy(url);
      }
    });
  }

  function eventsFn(info, successCallback, failureCallback) {
    if (cachedStart === info.startStr && cachedEnd === info.endStr) {
      successCallback(filterEvents(allEvents));
      return;
    }
    var params = new URLSearchParams({
      start: info.startStr,
      end: info.endStr,
    });
    fetch(eventsUrl + "?" + params.toString())
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (data) {
        allEvents = data;
        cachedStart = info.startStr;
        cachedEnd = info.endStr;
        successCallback(filterEvents(data));
        refreshAgendaIfActive();
      })
      .catch(failureCallback);
  }

  function datesSetFn(info) {
    var isListView = info.view.type === agendaViewType;
    agendaContainer.classList.toggle("hidden", !isListView);
    var harness = el.querySelector(".fc-view-harness");
    if (harness) harness.style.display = isListView ? "none" : "";
    if (isListView) {
      renderCustomAgenda(
        info.view.activeStart,
        info.view.activeEnd,
        filterEvents(allEvents),
      );
    }
    pushStateToUrl();
  }

  function eventClickFn(info) {
    var dUrl =
      info.event.extendedProps.detailUrl ||
      info.el.getAttribute("data-detail-url");
    if (dUrl) {
      info.jsEvent.preventDefault();
      window.location.href = dUrl;
    }
  }

  function eventDidMountFn(info) {
    info.el.style.cursor = "pointer";
    var dUrl = info.event.extendedProps.detailUrl;
    if (dUrl) {
      info.el.setAttribute("data-detail-url", dUrl);
    }
  }

  function buildCalendarOpts(localeData) {
    var opts = deepMerge(DEFAULT_CONFIG, options.config || {});
    opts.initialView = initialView;
    opts.events = eventsFn;
    opts.datesSet = datesSetFn;
    opts.eventClick = eventClickFn;
    opts.eventDidMount = eventDidMountFn;
    if (initialDate) {
      opts.initialDate = initialDate;
    }
    if (localeData) {
      opts.locale = localeData;
    }
    return opts;
  }

  var controller = {
    getCalendar: function () {
      return calendar;
    },
    getElement: function () {
      return el;
    },
    registerFilter: registerFilter,
    getFilterState: getFilterState,
    onFilterChange: onFilterChange,
    filterEvents: filterEvents,
    refresh: refresh,
  };

  // detailUrl is preserved for consumers that derive event URLs from it,
  // matching the original data-detail-url fallback used in eventClick.
  controller.detailUrl = detailUrl;

  initIcalButton();

  (options.filters || []).forEach(registerFilter);

  // Conditionally load the Slovenian locale based on the html lang attribute,
  // then build and render the calendar. Filters are registered synchronously
  // above so wrappers can read state immediately; getCalendar() stays null
  // until this resolves.
  var localePromise =
    lang === "sl"
      ? import(
          /* webpackInclude: /sl\.js$/ */
          "@fullcalendar/core/locales/sl"
        ).then(function (mod) {
          return mod.default;
        })
      : Promise.resolve(null);

  controller.ready = localePromise.then(function (localeData) {
    calendar = new Calendar(el, buildCalendarOpts(localeData));
    calendar.render();
    if (typeof options.onReady === "function") {
      options.onReady(controller);
    }
    return controller;
  });

  return controller;
}
